import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_LOCALE,
  detectBrowserLocale,
  isSupportedLocale,
  t,
  type SupportedLocale,
} from "./i18n";

const LocaleContext = createContext<SupportedLocale>(DEFAULT_LOCALE);

declare global {
  interface Window {
    __WILDSTREAM_LOCALE_UPDATE__?: (lang: string) => void;
  }
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<SupportedLocale>(detectBrowserLocale);
  const lockedRef = useRef(false);

  const updateLocale = useCallback((lang: string) => {
    if (lockedRef.current) return;
    if (!isSupportedLocale(lang)) return;
    setLocale(lang);
    lockedRef.current = true;
  }, []);

  useEffect(() => {
    window.__WILDSTREAM_LOCALE_UPDATE__ = updateLocale;
    return () => {
      delete window.__WILDSTREAM_LOCALE_UPDATE__;
    };
  }, [updateLocale]);

  return (
    <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>
  );
}

export function useLocale(): SupportedLocale {
  return useContext(LocaleContext);
}

export function useT(): (
  key: string,
  params?: Record<string, string | number>,
) => string {
  const locale = useContext(LocaleContext);
  return useCallback(
    (key: string, params?: Record<string, string | number>) =>
      t(locale, key, params),
    [locale],
  );
}
